/** Base reference size (HexagonBackground / solution demo). */
export const HEXAGON_MAP_HEX_SIZE = 50;
export const HEXAGON_MAP_HEX_MARGIN = 3;

export interface HexagonLayoutMetrics {
	hexagonWidth: number;
	hexagonHeight: number;
	hexagonMargin: number;
	computedMarginTop: number;
	contentInsetLeft: number;
	oddRowMarginLeft: number;
	evenRowMarginLeft: number;
	rowSpacing: number;
}

export type HexagonMapFitAxis = "width" | "height";

export function roundHexLayoutPx(value: number): number {
	return Math.round(value);
}

export function computeHexagonLayoutMetrics(
	hexagonSize = HEXAGON_MAP_HEX_SIZE,
	hexagonMargin = HEXAGON_MAP_HEX_MARGIN,
): HexagonLayoutMetrics {
	const hexagonWidth = hexagonSize;
	const hexagonHeight = hexagonSize * 1.1;
	const rowSpacing = hexagonSize * 0.8;
	const baseMarginTop = -36 - 0.275 * (hexagonSize - 100);
	const computedMarginTop = baseMarginTop + hexagonMargin;
	const oddRowMarginLeft = -(hexagonSize / 2);
	const evenRowMarginLeft = hexagonMargin / 2;
	const contentInsetLeft = Math.max(
		0,
		10 - Math.min(oddRowMarginLeft, evenRowMarginLeft),
	);
	return {
		hexagonWidth,
		hexagonHeight,
		hexagonMargin,
		computedMarginTop,
		contentInsetLeft,
		oddRowMarginLeft,
		evenRowMarginLeft,
		rowSpacing,
	};
}

/** Scale tile pitch so `rows` fill `containerHeight` and `cols` fill `containerWidth`. */
export function computeHexagonLayoutMetricsForGrid(
	rows: number,
	cols: number,
	containerWidth: number,
	containerHeight: number,
	fitAxis: HexagonMapFitAxis = "height",
	base: HexagonLayoutMetrics = computeHexagonLayoutMetrics(),
): HexagonLayoutMetrics {
	if (rows <= 0 || cols <= 0 || containerWidth <= 0 || containerHeight <= 0) {
		return base;
	}
	const topOffset = Math.max(0, -base.computedMarginTop);
	const heightDenom =
		topOffset +
		base.computedMarginTop +
		Math.max(0, rows - 1) * base.rowSpacing * 1.075 +
		base.hexagonHeight;
	const widthDenom =
		Math.max(1, cols) * (base.hexagonWidth + base.hexagonMargin);

	const scaleY = containerHeight / Math.max(heightDenom, 1);
	const scaleX = containerWidth / Math.max(widthDenom, 1);
	const scale = fitAxis === "width" ? scaleX : scaleY;

	const hexagonSize = Math.max(12, HEXAGON_MAP_HEX_SIZE * scale);
	return computeHexagonLayoutMetrics(hexagonSize, base.hexagonMargin * scale);
}

export function hexCellPosition(
	row: number,
	col: number,
	metrics: HexagonLayoutMetrics,
): { left: number; top: number } {
	const isOddRow = (row + 1) % 2 === 1;
	const rowLeftOffset = isOddRow
		? metrics.oddRowMarginLeft
		: metrics.evenRowMarginLeft;
	const topOffset = Math.max(0, -metrics.computedMarginTop);
	const top =
		topOffset +
		metrics.computedMarginTop +
		row * metrics.rowSpacing * 1.075;
	const left =
		metrics.contentInsetLeft +
		rowLeftOffset -
		10 +
		col * (metrics.hexagonWidth + metrics.hexagonMargin);
	return {
		left: roundHexLayoutPx(left),
		top: roundHexLayoutPx(top),
	};
}

export function hexCellCenter(
	row: number,
	col: number,
	metrics: HexagonLayoutMetrics,
): { left: number; top: number } {
	const pos = hexCellPosition(row, col, metrics);
	return {
		left: roundHexLayoutPx(pos.left + metrics.hexagonWidth / 2),
		top: roundHexLayoutPx(pos.top + metrics.hexagonHeight / 2),
	};
}

export function resolveHexagonInnerScale(
	hexagonWidth: number,
	hexagonMargin: number,
): number {
	if (hexagonWidth <= 0) {
		return 1;
	}
	return Math.max(0, (hexagonWidth - hexagonMargin * 2) / hexagonWidth);
}

/** Path stop icon diameter as a fraction of {@link HexagonLayoutMetrics.hexagonWidth}. */
export const HEXAGON_MAP_STOP_OVERLAY_WIDTH_RATIO = 0.37;

export const HEXAGON_MAP_STOP_OVERLAY_MIN_SIZE_PX = 24;

/**
 * Player doll **height** as a fraction of hex height — CSS box is the size SoT
 * (same lesson as scene `--scene-sprite-*-h`). ~1.17 ≈ prior 0.78 × 1.5.
 */
export const HEXAGON_MAP_CHARACTER_OVERLAY_HEIGHT_RATIO = 1.17;

/** @deprecated Prefer height ratio; kept for call-site clarity in docs/tests. */
export const HEXAGON_MAP_CHARACTER_OVERLAY_WIDTH_RATIO =
	HEXAGON_MAP_CHARACTER_OVERLAY_HEIGHT_RATIO;

/**
 * Feet sit below tile center by this fraction of the sprite box so the body
 * reads centered in the hex (feet-pin grows upward from this point).
 */
export const HEXAGON_MAP_CHARACTER_FEET_NUDGE_Y_RATIO = 0.28;

export const HEXAGON_MAP_CHARACTER_OVERLAY_MIN_SIZE_PX = 40;

/** Tile center → feet anchor Y (positive = down the screen). */
export function resolveHexagonCharacterFeetNudgeYPx(
	overlaySizePx: number,
): number {
	if (overlaySizePx <= 0) {
		return 0;
	}
	return Math.round(overlaySizePx * HEXAGON_MAP_CHARACTER_FEET_NUDGE_Y_RATIO);
}

export function resolveHexagonStopOverlaySizePx(hexagonWidth: number): number {
	if (hexagonWidth <= 0) {
		return HEXAGON_MAP_STOP_OVERLAY_MIN_SIZE_PX;
	}
	return Math.max(
		HEXAGON_MAP_STOP_OVERLAY_MIN_SIZE_PX,
		Math.round(hexagonWidth * HEXAGON_MAP_STOP_OVERLAY_WIDTH_RATIO),
	);
}

/**
 * Square CSS sprite box (scene stance aspect is 1). Pass `hexagonHeight` so
 * size tracks the tile, not stop-icon width.
 */
export function resolveHexagonCharacterOverlaySizePx(
	hexagonWidth: number,
	hexagonHeight: number = hexagonWidth * 1.1,
): number {
	if (hexagonHeight <= 0 && hexagonWidth <= 0) {
		return HEXAGON_MAP_CHARACTER_OVERLAY_MIN_SIZE_PX;
	}
	const referenceHeight =
		hexagonHeight > 0 ? hexagonHeight : hexagonWidth * 1.1;
	return Math.max(
		HEXAGON_MAP_CHARACTER_OVERLAY_MIN_SIZE_PX,
		Math.round(
			referenceHeight * HEXAGON_MAP_CHARACTER_OVERLAY_HEIGHT_RATIO,
		),
	);
}

export function computeHexagonMapContentBounds(
	rows: number,
	cols: number,
	metrics: HexagonLayoutMetrics = computeHexagonLayoutMetrics(),
): { width: number; height: number } {
	if (rows <= 0 || cols <= 0) {
		return { width: 0, height: 0 };
	}
	const maxRow = Math.max(0, rows - 1);
	const maxCol = Math.max(0, cols - 1);
	const bottomRight = hexCellPosition(maxRow, maxCol, metrics);
	const topOffset = Math.max(0, -metrics.computedMarginTop);
	return {
		width: bottomRight.left + metrics.hexagonWidth + metrics.hexagonMargin,
		height: Math.max(
			topOffset +
				metrics.computedMarginTop +
				maxRow * metrics.rowSpacing * 1.075 +
				metrics.hexagonHeight,
			bottomRight.top + metrics.hexagonHeight,
		),
	};
}

/** Minimum cols to fill container width at current row pitch. */
export function resolveDisplayColsForContainer(
	layoutCols: number,
	containerWidth: number,
	metrics: HexagonLayoutMetrics,
): number {
	if (containerWidth <= 0) {
		return layoutCols;
	}
	const cellPitchX = metrics.hexagonWidth + metrics.hexagonMargin;
	const fillCols = Math.ceil(containerWidth / cellPitchX);
	return Math.max(layoutCols, fillCols);
}

/** Minimum rows to fill container height at current row pitch. */
export function resolveDisplayRowsForContainer(
	layoutRows: number,
	containerHeight: number,
	metrics: HexagonLayoutMetrics,
): number {
	if (containerHeight <= 0) {
		return layoutRows;
	}
	let rows = Math.max(1, layoutRows);
	while (
		computeHexagonMapContentBounds(rows, 1, metrics).height <
		containerHeight
	) {
		rows += 1;
	}
	return rows;
}

/** Grid pitch sized to the requested fit axis, using the same metrics for layout and render. */
export function resolveHexagonMapDisplayMetrics(
	rows: number,
	layoutCols: number,
	containerWidth: number,
	containerHeight: number,
	fitAxis: HexagonMapFitAxis = "height",
): { displayRows: number; displayCols: number; metrics: HexagonLayoutMetrics } {
	if (containerWidth <= 0 || containerHeight <= 0) {
		const metrics = computeHexagonLayoutMetrics();
		return { displayRows: rows, displayCols: layoutCols, metrics };
	}

	const metrics = computeHexagonLayoutMetricsForGrid(
		rows,
		layoutCols,
		containerWidth,
		containerHeight,
		fitAxis,
	);
	const displayRows =
		fitAxis === "width"
			? resolveDisplayRowsForContainer(rows, containerHeight, metrics)
			: rows;
	const displayCols =
		fitAxis === "height"
			? resolveDisplayColsForContainer(
					layoutCols,
					containerWidth,
					metrics,
				)
			: layoutCols;
	return { displayRows, displayCols, metrics };
}
